import { createContext, ReactNode, useContext } from 'react'

import {
  ImpresionDocumentoProps,
  ImpresionFormatoProps,
  ImpresoraPuntoVentaProps,
} from '../interfaces/impresora.ts'
import type { EscposDocument, PrintResult } from '../sdk/isi-printer-client.ts'
import type { PdfFuenteProps } from '../services/impresionPdf.ts'
import type { IsiPrintEnvioOpciones } from '../services/isiPrintClient.ts'

/**
 * Motivo por el que se abre el diálogo de impresión en lugar de imprimir directo:
 * - isi-print-no-instalado: el agente no responde en este equipo (ofrecer descarga)
 * - isi-print-no-autorizado: el cajero aún no autorizó este sitio en el agente
 * - isi-print-actualizar: la versión instalada es menor a la mínima publicada
 * - sin-licencia: no hay licencia de impresión vigente (comercio o agente)
 * - sin-configuracion: no se pudo consultar `impresorasPuntoVenta`
 * - sin-asignacion: el punto de venta (ni la sucursal) tiene impresora para el documento
 * - impresora-no-encontrada: el agente no tiene la impresora asignada (PRINTER_NOT_FOUND)
 * - impresora-no-disponible: la impresora existe pero está apagada, sin papel, con la tapa abierta...
 * - error-impresion: el agente rechazó el trabajo
 * - dispositivo-no-soportado: teléfono o tableta, donde isi-print no se instala (menú de opciones)
 */
export type MotivoImpresionProps =
  | 'isi-print-no-instalado'
  | 'isi-print-no-autorizado'
  | 'isi-print-actualizar'
  | 'sin-licencia'
  | 'sin-configuracion'
  | 'sin-asignacion'
  | 'impresora-no-encontrada'
  | 'impresora-no-disponible'
  | 'error-impresion'
  | 'dispositivo-no-soportado'

/**
 * Quién recibe el documento (cliente, responsable de caja...). No interviene en la impresión: lo usan
 * las acciones extra del diálogo, por ejemplo enviar el PDF por WhatsApp al teléfono del cliente.
 */
export interface ImpresionDestinatarioProps {
  /** Nombre o razón social */
  nombre?: string | null
  /** Teléfono tal como está registrado (puede incluir +código de país) */
  telefono?: string | null
  /** Código de país sin '+', ej. "591" */
  codigoPais?: string | null
  email?: string | null
  /** Número visible del documento, ej. número de factura */
  numero?: string | number | null
}

/**
 * Lo que recibe una acción extra del diálogo de impresión (ver `accionesExtra` del provider).
 * Permite agregar botones (ej. "Enviar por WhatsApp") sin que este módulo dependa de otros.
 */
export interface ImpresionAccionExtraContextProps {
  documento: ImpresionDocumentoProps | null
  /** Título del diálogo, ej. "Factura #123" */
  titulo: string
  filename?: string
  destinatario?: ImpresionDestinatarioProps
  /** El documento habilita el envío por WhatsApp (`ImprimirDocumentoParams.whatsapp`) */
  whatsapp: boolean
  /**
   * URL pública (http/https) del PDF: la que se está viendo o, si es un PDF generado en el navegador,
   * la del formato carta. null si el documento solo existe en este navegador.
   */
  obtenerUrlPublica: () => Promise<string | null>
  /** El diálogo está imprimiendo o guardando */
  ocupado: boolean
}

export interface ImprimirDocumentoParams {
  /**
   * Destinatario del documento (cliente, responsable...). Lo usan las acciones extra del diálogo,
   * ej. enviar por WhatsApp al teléfono del cliente.
   */
  destinatario?: ImpresionDestinatarioProps
  /**
   * Muestra "Enviar por WhatsApp" en el diálogo de impresión (y en el menú móvil). Por defecto false.
   * Requiere que el proyecto conecte la acción (`accionesExtra` del provider) y licencia `lw` vigente.
   */
  whatsapp?: boolean
  documento: ImpresionDocumentoProps
  /** Título del diálogo y de los avisos. Ej. "Factura #123" */
  titulo?: string
  /**
   * Obtiene el PDF en el formato indicado: un Blob (pdfmake) o la URL de la representación gráfica.
   * Se llama con el formato de la asignación o, si no hay, con `formatoPorDefecto`.
   */
  obtenerPdf: (formato: ImpresionFormatoProps) => Promise<PdfFuenteProps> | PdfFuenteProps
  /**
   * Ticket ESC/POS equivalente. Si se indica y el formato asignado es de rollo, se imprime como
   * ESC/POS (más rápido, con corte); el PDF queda para la vista previa y el navegador.
   */
  obtenerEscpos?: () => Promise<EscposDocument> | EscposDocument
  /** Formato cuando el punto de venta no tiene asignación. Por defecto ROLLO */
  formatoPorDefecto?: ImpresionFormatoProps
  /**
   * Impresión automática (comanda al guardar, factura al emitir). En teléfonos y tabletas, donde no
   * hay isi-print, no se abre nada; sin esta marca se ofrece el menú para ver, compartir o enviar.
   */
  automatico?: boolean
  /**
   * Formato elegido por el usuario: prevalece sobre el de la asignación (ej. reimprimir una factura
   * en Carta aunque la asignación sea Rollo). La impresora y las copias siguen siendo las asignadas.
   */
  formato?: ImpresionFormatoProps
  /** Nombre del archivo enviado al agente */
  filename?: string
  /**
   * Clave anti-duplicado del documento (ej. `factura-<id>`): un doble clic o un reintento dentro de la
   * ventana del agente (60 s) no vuelve a imprimir si el anterior está en cola, imprimiendo o impreso.
   */
  idempotencyKey?: string
  /** Reimpresión intencional (botón "Reimprimir"): imprime aunque haya un duplicado reciente */
  reimpresion?: boolean
  /**
   * Se llama cuando el usuario imprime desde el diálogo (navegador o impresora). Con el navegador
   * solo indica que se abrió el diálogo de impresión del sistema, no que el papel haya salido.
   */
  onImpreso?: () => void
}

/** Datos para seguir un trabajo y, si falla, volver a abrir el diálogo con el documento */
export interface SeguimientoTrabajoProps {
  /** Ej. "Factura #123" */
  titulo: string
  /** Nombre visible de la impresora */
  impresora: string
  /** Documento a mostrar si el trabajo falla (sin params solo se avisa) */
  params?: ImprimirDocumentoParams
  pdf?: PdfFuenteProps | null
  formato?: ImpresionFormatoProps
}

export interface ResultadoImpresionProps {
  /** `true` si isi-print aceptó el trabajo (todas las copias) */
  impreso: boolean
  /** Motivo por el que se abrió el diálogo, si no se imprimió directo */
  motivo?: MotivoImpresionProps
  error?: unknown
}

export interface ImpresionDocumentoContextValue {
  /**
   * Imprime el documento con la impresora asignada al punto de venta (`copias` veces).
   * Si no se puede, abre el diálogo con el PDF, el aviso y la opción de guardar o vincular la impresora.
   * Nunca lanza errores de impresión.
   */
  imprimir: (params: ImprimirDocumentoParams) => Promise<ResultadoImpresionProps>
  /** Abre el diálogo como visor, sin imprimir automáticamente */
  abrirVisor: (params: ImprimirDocumentoParams) => Promise<void>
  /** Asignación resuelta del documento en el punto de venta de la sesión (o null) */
  obtenerAsignacion: (documento?: ImpresionDocumentoProps | null) => ImpresoraPuntoVentaProps | null
  /**
   * Envía el documento a una impresora de isi-print (`impresora.atributo1`: ID, nombre o IP), `copias` veces.
   * Con `escpos` se imprime el ticket ESC/POS en lugar del PDF. Lanza `PrinterError` si falla.
   * `PrintResult.duplicate` indica que el agente no reimprimió por la clave anti-duplicado.
   */
  enviarAImpresora: (
    pdf: PdfFuenteProps | null,
    printer: string,
    copias?: number,
    filename?: string,
    escpos?: EscposDocument | null,
    envio?: IsiPrintEnvioOpciones,
  ) => Promise<PrintResult>
  /**
   * Envía un documento exactamente como la impresión silenciosa: con impresora de rollo y ticket
   * ESC/POS disponible se imprime el ticket; si no, el PDF del formato indicado. La usan el diálogo
   * (Reintentar, Vincular, Guardar e imprimir) para que el resultado sea idéntico.
   * @returns El resultado del agente y el PDF enviado (null si fue ESC/POS).
   */
  enviarDocumento: (
    params: ImprimirDocumentoParams,
    destino: { printer: string; copias?: number },
    formato: ImpresionFormatoProps,
    envio?: IsiPrintEnvioOpciones,
  ) => Promise<{ resultado: PrintResult; pdf: PdfFuenteProps | null }>
  /**
   * Sigue un trabajo aceptado hasta que se imprima. Si se detiene (sin papel, apagada...) muestra un
   * aviso con Cancelar / Imprimir en otra; si falla, abre el diálogo de impresión.
   */
  seguirTrabajo: (resultado: PrintResult, info: SeguimientoTrabajoProps) => void
  /** Licencia de impresión vigente según el backend (`impresorasPuntoVenta`); null mientras no se conoce */
  licencia: boolean | null
  /** Mensaje del backend (ej. motivo de licencia no vigente) */
  mensaje: string | null
  cargando: boolean
  /** Botones adicionales del diálogo de impresión (prop `accionesExtra` del provider) */
  accionesExtra?: (ctx: ImpresionAccionExtraContextProps) => ReactNode
}

export const ImpresionDocumentoContext = createContext<ImpresionDocumentoContextValue | null>(null)

/**
 * Impresión de documentos (factura, nota de venta, estado de cuenta...) con la impresora asignada
 * al punto de venta, a través de isi-print. Requiere `ImpresionDocumentoProvider`.
 * @example
 * const impresion = useImpresionDocumento()
 * await impresion.imprimir({
 *   documento: 'FACTURA',
 *   titulo: `Factura #${numero}`,
 *   formatoPorDefecto: formatoDesdeTipoRepresentacion(user.tipoRepresentacionGrafica),
 *   obtenerPdf: (formato) => urlRepresentacionPorFormato(factura.representacionGrafica, formato),
 * })
 */
export const useImpresionDocumento = () => {
  const ctx = useContext(ImpresionDocumentoContext)
  if (!ctx) throw new Error('useImpresionDocumento debe usarse dentro de ImpresionDocumentoProvider')
  return ctx
}

export default useImpresionDocumento
